import { Component, ElementRef, OnInit, OnDestroy, ViewChild, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import {
  ConfirmationPopupService,
  ConfirmationPopupData,
  ConfirmationPopupVariant
} from '../../Provider/confirmation-popup.service';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-confirmation-popup',
  standalone: true,
  imports: [
    CommonModule,
    DialogModule,
    ButtonModule
  ],
  templateUrl: './confirmation-popup.component.html',
  styleUrl: './confirmation-popup.component.scss'
})
export class ConfirmationPopupComponent implements OnInit, OnDestroy {
  private confirmationPopupService = inject(ConfirmationPopupService);
  private destroy$ = new Subject<void>();

  visible = false;
  message = '';
  imageUrl: string | null = null;
  icon: string | null = null;
  variant: ConfirmationPopupVariant = 'default';
  confirmLabel = 'Yes';
  cancelLabel = 'No';
  denyLabel: string | null = null;
  showOnlyConfirm = false;
  destructiveConfirm = false;
  @ViewChild('safeButton') private safeButton?: ElementRef<HTMLButtonElement>;
  private currentPopup: ConfirmationPopupData | null = null;

  get dialogStyleClass(): string {
    const parts = ['confirmation-popup'];
    if (this.icon) parts.push('confirmation-popup--with-icon');
    if (this.variant !== 'default') parts.push(`confirmation-popup--${this.variant}`);
    return parts.join(' ');
  }

  get isMultiline(): boolean {
    return this.message.includes('\n');
  }

  get messageTitle(): string {
    if (!this.isMultiline) return this.message;
    return this.message.split('\n')[0] ?? this.message;
  }

  get messageBody(): string {
    if (!this.isMultiline) return '';
    return this.message.slice(this.messageTitle.length).replace(/^\n+/, '');
  }

  ngOnInit(): void {
    this.confirmationPopupService.popup$
      .pipe(takeUntil(this.destroy$))
      .subscribe((popup) => {
        if (popup) {
          this.currentPopup = popup;
          this.message = popup.message;
          this.imageUrl = popup.imageUrl ?? null;
          this.icon = popup.icon ?? null;
          this.variant = popup.variant ?? (popup.icon ? 'error' : 'default');
          this.confirmLabel = popup.confirmLabel ?? 'Yes';
          this.cancelLabel = popup.cancelLabel ?? 'No';
          this.denyLabel = popup.denyLabel?.trim() || null;
          this.showOnlyConfirm = popup.showOnlyConfirm ?? false;
          this.destructiveConfirm = popup.destructiveConfirm ?? false;
          this.visible = true;
        } else {
          this.visible = false;
          this.message = '';
          this.imageUrl = null;
          this.icon = null;
          this.variant = 'default';
          this.denyLabel = null;
          this.showOnlyConfirm = false;
          this.destructiveConfirm = false;
          this.currentPopup = null;
        }
      });
  }

  onDialogShow(): void {
    if (!this.destructiveConfirm) return;
    setTimeout(() => this.safeButton?.nativeElement.focus());
  }

  onOkOrClose(): void {
    this.confirmationPopupService.confirm();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onClose(): void {
    this.confirmationPopupService.close();
  }

  onCancelClick(): void {
    this.confirmationPopupService.close();
  }

  onDenyClick(): void {
    this.confirmationPopupService.deny();
  }

  onOkClick(): void {
    this.confirmationPopupService.confirm();
  }
}
